import { yupResolver } from '@hookform/resolvers/yup';
import { Delete } from '@mui/icons-material';
import { Box, IconButton, InputLabel, DialogContent, Grid, Stack } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Cell } from 'react-table';
import {
  ICommunicationServiceAccount,
  sharePhoneNumber,
  deleteCommunicationAccount,
  bulkUnAssign,
  usersAdvanceFilterQuery,
} from 'wisestep-api';
import {
  WsTypography,
  WsAutoComplete,
  WsButton,
  WsDialog,
  WsTypoVariants,
  WsDataTable,
} from 'wisestep-base';
import { array, object, string } from 'yup';

import { useWorkspaceContext } from '../../../context';
import {
  useSmsConfigAccessControl,
  SmsConfigAction,
} from '../../../hooks/useSmsConfigAccessControl';
import { success, failed } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { sharePhoneNumberPayload } from '../helper/payload';

import RechargeCommunicationCredits from './RechargeCommunicationCredits';

const schema = object().shape({
  assignedRecruiters: array()
    .of(string().email('Please enter a valid email'))
    .min(0)
    .max(5, 'You can only assign 5 recruiters'),
});

export interface ICommunicationAccountsProps {
  communicationAccounts: ICommunicationServiceAccount[];
  credit?: number;
  deleteable?: boolean;
  refresh: () => Promise<void>;
  refreshLocalStorage: () => Promise<void>;
  getAccounts: () => Promise<void>;
}

const CommunicationAccounts = ({
  communicationAccounts,
  credit,
  deleteable = false,
  refresh,
  refreshLocalStorage,
  getAccounts,
}: ICommunicationAccountsProps) => {
  const admin = useWorkspaceContext();
  const [openAssignInput, setOpenAssignInput] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState(false);
  const [openRechargeModal, setOpenRechargeModal] = useState(false);
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState<string>('');
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const [initialAssignedRecruiters, setInitialAssignedRecruiters] = useState<string[]>([]);
  const [recruiterSuggestions, setRecruiterSuggestions] = useState<string[]>([]);

  const setNotification = useNotificationStore((state) => state.setNotification);
  const { access } = useSmsConfigAccessControl([
    SmsConfigAction.MANAGE_SMS_CONFIGURATION,
    SmsConfigAction.DELETE_SMS_CONFIGURATION,
    SmsConfigAction.MANAGE_SMS_USERS,
  ]);
  const [manageSmsConfig, deleteSmsConfig, manageSmsUsers] = access;

  const defaultValues = {
    assignedRecruiters: initialAssignedRecruiters,
  };

  const confirm = (value: boolean) => {
    setOpenModal(value);
  };

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: defaultValues,
  });

  const handleAssign = useCallback(
    (index: number) => {
      setSelectedPhoneNumber(communicationAccounts[0].phoneNumbers[index].phoneNumber);
      setSelectedAccountId(communicationAccounts[0].id);
      setInitialAssignedRecruiters(communicationAccounts[0].phoneNumbers[index].sharedWith ?? []);
      setOpenModal(true);
      setOpenAssignInput(true);
    },
    [communicationAccounts]
  );

  const assignRecruiter = async () => {
    const selected = methods.getValues('assignedRecruiters');
    const response = await sharePhoneNumber({
      ...sharePhoneNumberPayload,
      accountId: selectedAccountId,
      emails: selected,
      phoneNumber: selectedPhoneNumber,
    });
    setOpenModal(false);
    if (response.data.status === 'SHARED') {
      setNotification(success('Phone Number shared with recruiter successfully!'));
    } else {
      setNotification(failed(response.data.status));
    }
    refreshLocalStorage();
  };

  const handleInput = async (input: string) => {
    const response = await usersAdvanceFilterQuery(input);
    setRecruiterSuggestions([]);
    response.data.map((recruiter) =>
      setRecruiterSuggestions((prev) => [...prev, recruiter?.userId])
    );
  };

  const clickBulkUnAssign = async () => {
    const selected = methods.getValues('assignedRecruiters');
    const response = await bulkUnAssign({
      emails: selected,
    });
    setOpenModal(false);
    if (response.data) {
      setNotification(success('Email has been unassigned successfully!'));
    } else {
      setNotification(failed('Error: while unassigning Email!'));
    }
    refreshLocalStorage();
  };

  const handleDeleteAccount = useCallback(
    async (index: number) => {
      await deleteCommunicationAccount(communicationAccounts[index].id ?? '');
      getAccounts();
    },
    [communicationAccounts, getAccounts]
  );

  const columns = useMemo(
    () => [
      {
        Header: 'ID',
        accessor: 'id',
        Cell: (cell: Cell) => (
          <Stack direction="row" spacing={2}>
            <WsTypography>{cell.value}</WsTypography>{' '}
            {cell.value && deleteable && (admin.owns || deleteSmsConfig) && (
              <IconButton onClick={() => handleDeleteAccount(cell.row.index)}>
                <Delete />
              </IconButton>
            )}
          </Stack>
        ),
      },
      {
        Header: 'Phone Numbers',
        accessor: 'phoneNumber',
      },
      {
        Header: 'SMS',
        accessor: 'sms',
      },
      {
        Header: 'Call',
        accessor: 'call',
      },
      {
        Header: 'Assign/Un-assign',
        accessor: 'sharedWith',
        Cell: (cell: Cell) =>
          admin.owns || manageSmsConfig || manageSmsUsers ? (
            <WsButton variant="outlined" onClick={() => handleAssign(cell.row.index)}>
              {`Assign (${cell.value}/5)`}
            </WsButton>
          ) : (
            <WsButton
              disabled={true}
              variant="outlined"
              onClick={() => handleAssign(cell.row.index)}
            >
              {`Assign (${cell.value}/5)`}
            </WsButton>
          ),
      },
    ],
    [
      admin.owns,
      deleteSmsConfig,
      deleteable,
      handleAssign,
      handleDeleteAccount,
      manageSmsConfig,
      manageSmsUsers,
    ]
  );

  const unAssign = () => {
    setOpenAssignInput(false);
    setInitialAssignedRecruiters([]);
    setRecruiterSuggestions([]);
    setOpenModal(true);
  };

  const data = useMemo(
    () =>
      communicationAccounts[0]?.phoneNumbers.map((account, i) => ({
        id: i === 0 ? communicationAccounts[0].serviceAccountId : '',
        phoneNumber: account.phoneNumber ? account.phoneNumber : '',
        sms: account.canSendSms ? 'Yes' : 'No',
        call: 'Coming soon',
        sharedWith: account.sharedWith?.length ?? 0,
      })) ?? [],
    [communicationAccounts]
  );

  const handleRecruiterTagsChange = (tags) => {
    methods.setValue('assignedRecruiters', tags);
    setInitialAssignedRecruiters(tags);
  };

  return (
    <FormProvider {...methods}>
      {openRechargeModal && (
        <RechargeCommunicationCredits onClose={() => setOpenRechargeModal(false)} />
      )}
      <Stack direction="row" justifyContent="space-between" p={2}>
        {credit ? (
          <WsTypography bold={true}>{`Credits earned: ${credit}`}</WsTypography>
        ) : (
          <div></div>
        )}
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          {(admin.owns || manageSmsConfig || manageSmsUsers) && (
            <>
              {/* <WsButton onClick={() => setOpenRechargeModal(true)}>Recharge SMS credits</WsButton> */}
              <WsButton onClick={refresh}>Refresh Account</WsButton>
              <WsButton onClick={() => unAssign()}>Bulk Un-Assign</WsButton>
            </>
          )}
        </Stack>
      </Stack>
      <form onSubmit={methods.handleSubmit(assignRecruiter)}>
        <Box>
          <WsDataTable columns={columns} data={data} boldItems={['phoneNumber', 'sms', 'call']} />
          {false && (admin.owns || manageSmsConfig || manageSmsUsers) && (
            <Stack spacing={1} py={2}>
              <InputLabel>{`Select Recruiters To Assign Phone Number: ${selectedPhoneNumber}`}</InputLabel>
              <WsAutoComplete
                name="assignedRecruiters"
                fullWidth
                size="small"
                multiple={true}
                initialValue={initialAssignedRecruiters}
                value={initialAssignedRecruiters}
                optionsInStringArray={recruiterSuggestions}
                onValueEnter={(input) => handleInput(input)}
                onTagsChange={(tags) => handleRecruiterTagsChange(tags)}
              />
              <div>
                <WsButton size="large" type="submit">
                  Save
                </WsButton>
              </div>
            </Stack>
          )}
        </Box>
      </form>
      <WsDialog size="medium" openDialog={openModal}>
        <DialogContent sx={{ p: 2 }}>
          {openAssignInput ? (
            <WsTypography variant={WsTypoVariants.TITLE}>
              Select Recruiters to Assign Phone Number : {selectedPhoneNumber}
            </WsTypography>
          ) : (
            <WsTypography variant={WsTypoVariants.TITLE}>
              Select Recruiters to Bulk Un-assign Phone Numbers
            </WsTypography>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} mt={2}>
              <Stack direction="column">
                <WsTypography variant={WsTypoVariants.SUB_TITLE}>Email Address</WsTypography>
              </Stack>
              <WsAutoComplete
                name="assignedRecruiters"
                fullWidth
                size="small"
                multiple={true}
                value={initialAssignedRecruiters}
                initialValue={initialAssignedRecruiters}
                optionsInStringArray={recruiterSuggestions}
                onValueEnter={(input) => handleInput(input)}
                onTagsChange={(tags) => handleRecruiterTagsChange(tags)}
              />
            </Grid>
            <Grid item xs={12} spacing={2}>
              <Stack direction="row" justifyContent="flex-end" spacing={2}>
                <WsButton variant="outlined" size="large" onClick={() => confirm(false)}>
                  Cancel
                </WsButton>
                {openAssignInput ? (
                  <WsButton type="submit" size="large" onClick={() => assignRecruiter()}>
                    Assign
                  </WsButton>
                ) : (
                  <WsButton type="submit" size="large" onClick={() => clickBulkUnAssign()}>
                    Un Assign
                  </WsButton>
                )}
              </Stack>
            </Grid>
          </Grid>
        </DialogContent>
      </WsDialog>
    </FormProvider>
  );
};

export default CommunicationAccounts;
